import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../services/authService";
import "../../assets/styles/AuthForm.css";
import "../../assets/styles/bootstrap.min.css";
import i1 from "../../assets/image/i1.png";
import logo from "../../assets/image/logo.png";
import { ROUTES } from "../../utils/constants";
import Footer from "../../components/Layout/Footer";

const PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!#%*?&^]{8,}$/;

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successToast, setSuccessToast] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // 🔐 Validate token on load
    useEffect(() => {
        if (!token) {
            setError("Invalid or missing reset token.");
        }
    }, [token]);

    const validateForm = (): boolean => {
        if (!newPassword || !confirmPassword) {
            setError("All fields are required.");
            return false;
        }

        if (!PASSWORD_REGEX.test(newPassword)) {
            setError(
                "Password must be at least 8 characters long and include uppercase, lowercase, number and special character."
            );
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        if (!token || !validateForm()) return;

        try {
            setLoading(true);

            await authService.resetPassword({
                token,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });

            setSuccessToast(true);
            setNewPassword("");
            setConfirmPassword("");

            setTimeout(() => {
                navigate(ROUTES.SIGNIN);
            }, 3000);

        } catch (err: unknown) {
            console.log("Reset password error:", err); // Debug log
            setError("Unexpected error occurred.");

        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page" style={{ overflowY: "auto", height: "100vh" }}>
            <div className="auth-form-container">
                <div className="auth-decorative-panel">
                    <div className="decorative-content">
                        <img src={i1} alt="Corecrate Illustration" />
                    </div>
                </div>

                <div className="auth-form-wrapper mr-5 ml-5">
                    <img
                        src={logo}
                        alt="Corecrate Logo"
                        className="mb-5"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/")}
                    />

                    <p className="text-muted mb-4">
                        Please enter your new password below.
                    </p>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="password">New Password</label>
                            <div className="password-wrapper">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        right: "5px"
                                    }}
                                    className="eye-icon"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? "hide" : "show"}
                                </span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-wrapper">
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Re-enter new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <span
                                    className="eye-icon"
                                    style={{
                                        position: "absolute",
                                        right: "5px"
                                    }}
                                    onClick={() =>
                                        setShowConfirmPassword(!showConfirmPassword)
                                    }
                                >
                                    {showConfirmPassword ? "hide" : "show"}
                                </span>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button
                            type="submit"
                            disabled={loading}
                            className="submit-button"
                        >
                            {loading ? "Resetting..." : "Update Password"}
                        </button>

                        <div className="form-footer">
                            Nevermind,<Link to={ROUTES.SIGNIN}> I remembered it.</Link>
                        </div>
                    </form>
                </div>
            </div>

            {successToast && (
                <div className="toast-overlay">
                    <div className="toast-box">
                        <div className="toast-icon">✓</div>
                        <p className="toast-text">
                            Password reset successfully.
                        </p>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default ResetPasswordPage;